/**
 * Why every dossier field on the desk is still empty.
 *
 * Coverage tells an operator what the deal record holds. It says nothing about
 * what the DOCUMENTS hold, so a field reads MISSING on a deal whose package
 * plainly states it — and reporting "ask the broker" and "we failed to read it"
 * in the same amber is how somebody learns to ignore the colour.
 *
 * This separates them. The gate line is the count of datapoints a document
 * holds and the dossier does not show: ours to fix, and the only number here
 * that should ever go down. Everything else is either somebody else's move or
 * finished work, and is reported as such rather than as a defect.
 *
 * Every finding names the file and the exact place inside it, because "the OM
 * states the LTV" sends a person to read ninety pages. Nothing is applied —
 * a candidate is an offer, and confirming one is a separate, audited act in
 * the Data Intelligence Review.
 */
import { AlertTriangle, CheckCircle2, ChevronRight, Search, UserCheck } from 'lucide-react'

import type { FieldGap, GapCensus, GapCensusDesk, GapResolution } from '@/services/ingestion'

/** The resolutions, in the order of who has to move next. */
const RESOLUTIONS: { key: GapResolution; label: string; who: string; ours: boolean }[] = [
  { key: 'value_available', label: 'A source states it, the dossier does not hold it', who: 'us', ours: true },
  { key: 'label_seen_unparsed', label: 'A line is labeled for it and no reading was admissible', who: 'us', ours: true },
  { key: 'no_search_route', label: 'The sources could not be searched', who: 'us', ours: true },
  { key: 'needs_review', label: 'Offered under a label several fields share, or a model that needs ratifying', who: 'a reviewer', ours: false },
  { key: 'derivable_when', label: 'A formula gives it once its inputs arrive', who: 'the inputs', ours: false },
  { key: 'absent_from_package', label: 'Every source read in full; nothing resembles it', who: 'the broker', ours: false },
  { key: 'desk_assigned', label: 'The desk sets this; no package states it', who: 'nobody', ours: false },
  { key: 'not_applicable', label: 'This asset class cannot have it, or a reviewer closed it', who: 'nobody', ours: false },
]

const OURS = new Set(RESOLUTIONS.filter((r) => r.ours).map((r) => r.key))

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** The document and the exact place inside it, as one readable phrase.
 *
 *  Not exported: the phrase is asserted through the rendered report, because
 *  what matters is that an operator reading the worklist can see where to go. */
function whereRead(gap: FieldGap): string | null {
  const found = gap.found.find((f) => f.admissible && f.precise) ?? gap.found[0]
  if (!found) return null
  const file = found.doc?.filename ?? 'the package'
  const at = found.at ?? {}
  if (at.sheet) return `${file} · ${at.sheet}!${at.cell ?? ''}`
  if (at.lineNo) return `${file} · line ${at.lineNo}${at.page ? `, page ${at.page}` : ''}`
  return file
}

/** Both completeness figures for one deal, each saying what it counts. The
 *  bare '45/134' was the intake record's projection beside a pipeline reading
 *  '48 of 134' for the same deal; the deal figure is that pipeline measure. */
function dealFigures(deal: GapCensus): string {
  const record = `${deal.populated}/${deal.total} on the intake record`
  if (deal.populatedOnDeal == null) return `${record} · not yet a deal`
  return `${record} · ${deal.populatedOnDeal}/${deal.total} on the deal`
}

type Props = {
  census: GapCensusDesk
  onOpenRecord: (ingestionId: string) => void
}

export default function GapCensusReport({ census, onOpenRecord }: Props) {
  const pct = census.catalogue
    ? Math.round((census.populated / census.catalogue) * 1000) / 10
    : 0
  const worklist = census.deals
    .map((deal) => ({
      deal,
      gaps: deal.gaps.filter((g) => OURS.has(g.resolution)),
    }))
    .filter((row) => row.gaps.length > 0)

  return (
    <div className="space-y-3" data-testid="gap-census-report">
      <div className="border-b border-amber-400/10 pb-2">
        <h3 className="flex items-center gap-1.5 text-[12px] font-medium text-stone-200">
          {census.engineMisses === 0
            ? <CheckCircle2 size={13} className="text-emerald-300/90" />
            : <AlertTriangle size={13} className="text-amber-300" />}
          Field gap census
        </h3>
        <p className="mt-0.5 text-[10px] text-stone-500">
          {plural(census.deals.length, 'deal', 'deals')} · {census.populated} of{' '}
          {census.catalogue} fields populated on the intake records ({pct}%) ·{' '}
          {census.elapsedSeconds}s
        </p>
        <p
          className={`mt-1.5 text-[11px] ${
            census.engineMisses === 0 ? 'text-emerald-300/90' : 'text-amber-300'
          }`}
          data-testid="gap-census-gate"
        >
          {census.engineMisses === 0
            ? 'Nothing in these packages is stated and unread.'
            : `${plural(census.engineMisses, 'datapoint is', 'datapoints are')} stated in a source and not held on the deal.`}
        </p>
        {!census.complete && census.remaining?.length > 0 && (
          <p className="mt-1 text-[10px] text-amber-300/90">
            {plural(census.remaining.length, 'record was', 'records were')} not reached in
            this run. Run it again to continue.
          </p>
        )}
      </div>

      {/* ── Why the rest are empty ── */}
      <div>
        <h4 className="mb-1 text-[10px] uppercase tracking-wide text-stone-500">
          Why a field is empty
        </h4>
        <div className="space-y-0.5">
          {RESOLUTIONS.filter((r) => (census.totals[r.key] ?? 0) > 0).map((r) => (
            <div key={r.key} className="flex items-baseline gap-2 text-[10px]">
              <span
                className={`w-10 shrink-0 text-right font-mono ${
                  r.ours ? 'text-amber-300' : 'text-stone-400'
                }`}
              >
                {census.totals[r.key]}
              </span>
              <span className="text-stone-400">{r.label}</span>
              <span className="ml-auto shrink-0 text-stone-600">{r.who}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── The worklist ── */}
      {worklist.length > 0 && (
        <div>
          <h4 className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wide text-stone-500">
            <Search size={10} /> Stated in a source, not on the deal
          </h4>
          <div className="space-y-2">
            {worklist.map(({ deal, gaps }) => (
              <div key={deal.ingestionId} className="rounded border border-amber-400/10 p-2">
                <button
                  type="button"
                  onClick={() => onOpenRecord(deal.ingestionId)}
                  className="flex w-full items-center gap-1 text-left text-[11px] text-stone-300 hover:text-amber-300"
                >
                  <span className="font-mono">{deal.loanId ?? deal.ingestionId}</span>
                  <span className="text-stone-600" data-testid="gap-census-deal-figures">
                    {dealFigures(deal)}
                  </span>
                  <ChevronRight size={11} className="ml-auto" />
                </button>
                <div className="mt-1 space-y-0.5">
                  {gaps.map((gap) => (
                    <div key={gap.field} className="text-[10px] leading-relaxed">
                      <span className="text-stone-600">{gap.page}</span>{' '}
                      <span className="text-stone-300">{gap.label ?? gap.field}</span>
                      {' — '}
                      <span className="text-stone-500">{gap.why}</span>
                      {whereRead(gap) && (
                        <span className="ml-1 font-mono text-stone-600">{whereRead(gap)}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="flex items-start gap-1.5 border-t border-amber-400/10 pt-2 text-[10px] leading-relaxed text-stone-500">
        <UserCheck size={11} className="mt-px flex-shrink-0" />
        <span>
          Nothing here has been applied. Every reading is an offer carrying the exact
          place it was read from; accepting one is a decision you make in the Data Intelligence Review,
          field by field.
        </span>
      </p>
    </div>
  )
}
